import styled from '@emotion/styled';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useNavigation } from '@hotjar/navigation';
import type { AggregatedResult } from '@hotjar/feedback/domain/Surveys/Results/AggregatedResult';
import { Link, Text, tokens } from '@hotjar/hotsauce';
import { EmotionIcon, useEmotionLabels } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import type { EmotionScore } from '@hotjar/insights-common/constants/emotions';
import { EmotionScores } from '@hotjar/insights-common/constants/emotions';
import type { ReactionStyle } from '@hotjar/feedback/domain/Surveys/Questions/SurveyQuestion';
import { useCurrentSiteId } from '@hotjar/site';
import { useTranslation, formatPercentage } from '@hotjar/translations';

export interface ReactionSummaryProps {
  results: AggregatedResult;
  reactionStyle: ReactionStyle;
  surveyId: number;
}

export interface AverageSentiment {
  sentiment: number;
  emotion: EmotionScore;
}

const ReactionSummaryWrapper = styled.div`
  display: flex;
  gap: 100px;
  margin: 0 auto;
  max-width: 960px;
`;

const Emotions = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr auto;
  border-collapse: collapse;
`;

const Emotion = styled.aside`
  display: contents;
`;

const ProgressBarWrapper = styled.div`
  display: contents;
`;

interface PercentageProps {
  percentage: number;
}

const PercentageWrapper = styled.div<PercentageProps>`
  width: calc(${({ percentage }) => percentage}*100%);
  position: relative;
  display: flex;
  margin-bottom: 16px;
`;

const VisibleParts = styled.div`
  grid-column: 1;
`;

const ProgressBar = styled.div`
  background: ${tokens.BRAND_COLORS.cobalt_800};
  height: ${tokens.spacer.x1};
  border-radius: ${tokens.spacer.x1};
  width: 100%;
`;

const StyledEmotionIcon = styled(EmotionIcon)`
  margin-right: ${tokens.spacer.x1};
`;

const StyledReactionAverage = styled(Text)`
  font-size: 116px;
  display: block;
  line-height: 1;
`;

const EmotionsSummary = styled.div`
  flex: 1;
  margin: 0 ${tokens.spacer.x1};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StyledResponsesCount = styled(Link)`
  ${tokens.text('body1')}
`;

const VisibleText = styled.div`
  white-space: nowrap;
  position: absolute;
  left: calc(100% + ${tokens.spacer.x1});
  top: -6px;
`;

const HiddenText = styled.div`
  grid-column: 2;
  white-space: nowrap;
  padding-left: ${tokens.spacer.x1};
  visibility: hidden;
`;

const EmotionLabel = styled(Text)`
  margin-bottom: ${tokens.spacer.x1};
`;

export function getAverageSentiment(
  answers: Record<string, number>,
  total: number,
): AverageSentiment {
  const sum = EmotionScores.reduce((acc: number, score) => {
    const key = score + 1;
    const currentTotal = key * answers[key];
    return acc + currentTotal;
  }, 0);

  const sentiment = sum / total;

  return {
    sentiment,
    emotion: Math.round(sentiment - 1) as EmotionScore,
  };
}

const useNavigateToResponses = (surveyId: number) => {
  const navigate = useNavigate();
  const { buildHrefFromRoute, routes } = useNavigation();
  const siteId = useCurrentSiteId();

  return useCallback(() => {
    if (siteId === null) throw new Error('Site ID is not defined');
    navigate(
      buildHrefFromRoute(routes.SURVEYS.RESPONSES.path, {
        siteId,
        surveyId,
      }),
    );
  }, [navigate, buildHrefFromRoute, routes, siteId, surveyId]);
};

export function ReactionSummary({
  results,
  reactionStyle = 'default',
  surveyId,
}: ReactionSummaryProps): JSX.Element {
  const onResponsesClicked = useNavigateToResponses(surveyId);

  const average = useMemo(
    () => getAverageSentiment(results.answers, results.total),
    [results.answers, results.total],
  );
  const { t, i18n } = useTranslation('feedback');
  const emotionLabels = useEmotionLabels();

  const emotions = useMemo(
    () =>
      EmotionScores.map((score) => ({
        score,
        label: emotionLabels[score],
        percentage: Math.round(results.pc_answers[score + 1]),
        count: results.answers[score + 1],
      })),
    [emotionLabels, results.pc_answers, results.answers],
  );

  const maxPercentage = useMemo(
    () => Math.max(...emotions.map((emotion) => emotion.percentage)),
    [emotions],
  );

  return (
    <ReactionSummaryWrapper>
      <EmotionsSummary>
        <Text textType="subtitle1">
          <StyledEmotionIcon emotionStyle={reactionStyle} score={average.emotion} size="m" />
          {t('results.average')}
        </Text>
        <StyledReactionAverage as="h1">
          {Intl.NumberFormat(i18n.language, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }).format(average.sentiment)}
        </StyledReactionAverage>
        <StyledResponsesCount as="button" kind="inline" onClick={onResponsesClicked}>
          {t('results.response_count', { count: results.num_answers })}
        </StyledResponsesCount>
      </EmotionsSummary>
      <Emotions>
        {emotions.map((emotion) => (
          <Emotion
            key={emotion.score}
            aria-label={`${t('common.emotion.label.emotion', {
              value: emotion.label,
            })} ${formatPercentage(emotion.percentage, i18n)} ${t('results.responses', {
              count: emotion.count,
            })}`}
          >
            <EmotionLabel textType="subtitle2">
              <StyledEmotionIcon emotionStyle={reactionStyle} score={emotion.score} size="s" />
              {emotion.label}
            </EmotionLabel>
            <ProgressBarWrapper>
              <VisibleParts>
                <PercentageWrapper percentage={emotion.percentage / maxPercentage}>
                  <ProgressBar />
                  <VisibleText>
                    <Text textType="body2">{formatPercentage(emotion.percentage, i18n)}</Text>
                    &nbsp;
                    <Text textType="body2" textColor="text_light">
                      {t('results.responses', {
                        count: emotion.count,
                      })}
                    </Text>
                  </VisibleText>
                </PercentageWrapper>
              </VisibleParts>
              <HiddenText aria-hidden>
                <Text textType="body2">{formatPercentage(emotion.percentage, i18n)}</Text>
                &nbsp;
                <Text textType="body2" textColor="text_light">
                  {t('results.responses', {
                    count: emotion.count,
                  })}
                </Text>
              </HiddenText>
            </ProgressBarWrapper>
          </Emotion>
        ))}
      </Emotions>
    </ReactionSummaryWrapper>
  );
}
